import React, { useEffect, useReducer } from "react";
import interact from "interactjs";
import { useDispatch } from "react-redux";
import {
  tap,
  editObject,
  interactionStart,
  moveMultipleObjects,
  mouseEnter,
  mouseLeave,
} from "../reducers/slices/phases.js";
import useCurrentValue from "../../hooks/useCurrentValue.js";

import DragCircle from "./DragCircle.js";
import DragLine from "./DragLine.js";

import Helper from "../functions/Helper.js";
import { getBoundingRect } from "../functions/collisionChecking.js";
import { useTheme } from "@mui/material/styles";

const SVGToScreen = ({ x, y }) => {
  var svg = document.getElementById("editor_court");
  var p = svg.createSVGPoint();
  p.x = x;
  p.y = y;
  return p.matrixTransform(svg.getScreenCTM());
};

const resizeDeltas = (dragDelta) => {
  return {
    top: {
      x: 0,
      y: -dragDelta.y,
    },
    bottom: {
      x: 0,
      y: dragDelta.y,
    },
    left: {
      x: -dragDelta.x,
      y: 0,
    },
    right: {
      x: dragDelta.x,
      y: 0,
    },
    "top-left": {
      x: -dragDelta.x,
      y: -dragDelta.y,
    },
    "top-right": {
      x: dragDelta.x,
      y: -dragDelta.y,
    },
    "bottom-left": {
      x: -dragDelta.x,
      y: dragDelta.y,
    },
    "bottom-right": {
      x: dragDelta.x,
      y: dragDelta.y,
    },
  };
};

function getOrigin(edge, rect) {
  const { x, y, width, height } = rect;
  switch (edge) {
    case "top":
      return {
        x: x + width / 2,
        y: y + height,
      };
    case "bottom":
      return {
        x: x + width / 2,
        y: y,
      };
    case "left":
      return {
        x: x + width,
        y: y + height / 2,
      };
    case "right":
      return {
        x: x,
        y: y + height / 2,
      };
    case "top-left":
      return {
        x: x + width,
        y: y + height,
      };
    case "top-right":
      return {
        x: x,
        y: y + height,
      };
    case "bottom-left":
      return {
        x: x + width,
        y: y,
      };
    case "bottom-right":
      return {
        x: x,
        y: y,
      };
    default:
      return {
        x: x + width / 2,
        y: y + height / 2,
      };
  }
}

function applyTransform(point, matrix) {
  const [a, b, c, d, e, f, , ,] = matrix.flat();
  const [x, y] = point;
  const newX = a * x + c * y + e;
  const newY = b * x + d * y + f;
  return [newX, newY];
}

function shiftObjectByDelta(delta) {
  return [[1, 0, 0, 1, delta.x, delta.y]];
}

function scaleObjectFromOrigin(scale, origin) {
  const { x: scaleX, y: scaleY } = scale;
  const { x: originX, y: originY } = origin;
  const tx = (1 - scaleX) * originX;
  const ty = (1 - scaleY) * originY;

  return [scaleX, 0, 0, scaleY, tx, ty];
}

function rotateObjectAroundOrigin(angle, origin) {
  const { x: originX, y: originY } = origin;
  const radAngle = (angle * Math.PI) / 180;
  const cos = Math.cos(radAngle);
  const sin = Math.sin(radAngle);
  const tx = originX * (1 - cos) + originY * sin;
  const ty = originY * (1 - cos) - originX * sin;

  return [cos, sin, -sin, cos, tx, ty];
}

const transformPoints = (points, index, dragDelta) => {
  let newPoints = [...points];
  newPoints[index] = [
    newPoints[index][0] + dragDelta.x,
    newPoints[index][1] + dragDelta.y,
  ];

  return newPoints;
};

const getSVGPathFromPoints = (points) => {
  let path = "";
  points.forEach((point, index) => {
    if (index === 0) {
      path += `M ${point[0]} ${point[1]} `;
    } else {
      path += `L ${point[0]} ${point[1]} `;
    }
  });
  path += "Z";
  return path;
};

const initialState = {
  dragDelta: { x: 0, y: 0 },
  activeEdge: null,
  editMode: null,
  editPoint: null,
  transform: null,
  rotate: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "NEW_DRAG_DELTA":
      return {
        ...state,
        dragDelta: action.payload,
      };

    case "MOVE_DRAG_DELTA":
      return {
        ...state,
        dragDelta: {
          x: state.dragDelta.x + action.payload.x,
          y: state.dragDelta.y + action.payload.y,
        },
      };

    case "START_RESIZE":
      return {
        ...state,
        activeEdge: action.payload.edge,
        editMode: "RESIZE",
        transform: action.payload.transform,
      };

    case "END_ACTION":
      return initialState;

    case "START_DRAG":
      return {
        ...state,
        editMode: "DRAG",
      };

    case "START_MOVE_POINT":
      return {
        ...state,
        editMode: "MOVE_POINT",
        editPoint: action.payload.index,
      };

    case "START_ROTATE":
      return {
        ...state,
        editMode: "ROTATE",
        rotate: {
          ...action.payload,
          currentAngle: 0,
        },
      };

    case "ROTATE_MOVE":
      return {
        ...state,
        rotate: {
          ...state.rotate,
          ...action.payload,
          currentAngle:
            Helper.getAngle(
              state.rotate.centerPageCoords,
              state.rotate.clientCoords
            ) - state.rotate.startAngle,
        },
      };

    default:
      return state;
  }
};

const getOutputPoints = (points, state) => {
  let outputPoints = [...points];
  let transformations = [];

  if (state.editMode === "DRAG") {
    transformations.push(shiftObjectByDelta(state.dragDelta));
  } else if (state.editMode === "MOVE_POINT") {
    outputPoints = transformPoints(
      outputPoints,
      state.editPoint,
      state.dragDelta
    );
  } else if (state.editMode === "RESIZE") {
    transformations.push(
      scaleObjectFromOrigin(
        {
          x:
            1 +
            resizeDeltas(state.dragDelta)[state.activeEdge].x /
              state.transform.width,
          y:
            1 +
            resizeDeltas(state.dragDelta)[state.activeEdge].y /
              state.transform.height,
        },
        state.transform.origin
      )
    );
  }

  outputPoints = outputPoints.map((point) => {
    transformations.forEach((transformation) => {
      point = applyTransform(point, transformation);
    });
    return point;
  });

  return outputPoints;
};

export default function Polygon(props) {
  let {
    id,
    angle,
    uId,
    points,
    backgroundColor,
    status,
    pixelPerFoot,
    editable = false,
    court,
  } = props;

  const theme = useTheme();
  const reduxDispatch = props.dispatch;
  const [state, dispatch] = useReducer(reducer, initialState);
  const pixelPerFootRef = useCurrentValue(pixelPerFoot);
  const statusRef = useCurrentValue(status);
  const pointsRef = useCurrentValue(points);
  const courtRef = useCurrentValue(court);
  const stateRef = useCurrentValue(state);
  const rectBeforeTransforms = getBoundingRect(points, 0);
  const rectBeforeTransformsRef = useCurrentValue(rectBeforeTransforms);

  useEffect(() => {
    if (editable) {
      interact("#" + uId)
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          if (statusRef.current !== "EDIT") {
            reduxDispatch(
              tap({
                id: id,
                ctrlKey:
                  event.originalEvent.ctrlKey || event.originalEvent.metaKey,
              })
            );
          }
        })
        .on("tap", (event) => {
          if (statusRef.current !== "EDIT") {
            reduxDispatch(
              tap({
                id: id,
                ctrlKey:
                  event.originalEvent.ctrlKey || event.originalEvent.metaKey,
              })
            );
          }
        })
        .on("doubletap", () => {
          if (statusRef.current !== "EDIT") {
            reduxDispatch(
              editObject({ actionId: id, changes: { status: "EDIT" } })
            );
          }
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            dispatch({ type: "START_DRAG" });
            reduxDispatch(interactionStart({ id: id }));
          },
          onmove: (event) => {
            var deltaX = event.dx / pixelPerFootRef.current;
            var deltaY = event.dy / pixelPerFootRef.current;
            dispatch({
              type: "MOVE_DRAG_DELTA",
              payload: { x: deltaX, y: deltaY },
            });
          },
          onend: () => {
            reduxDispatch(
              moveMultipleObjects({
                objects: [{ id, points: pointsRef.current }],
                x: courtRef.current.full
                  ? -stateRef.current.dragDelta.y
                  : stateRef.current.dragDelta.x,
                y: courtRef.current.full
                  ? stateRef.current.dragDelta.x
                  : stateRef.current.dragDelta.y,
              })
            );
            dispatch({ type: "END_ACTION" });
          },
        });
      return () => interact("#" + uId).unset();
    }
  }, []); //eslint-disable-

  const getResizeProps = (edge, angle) => ({
    onStart: () => {
      let nextOrigin = getOrigin(edge, rectBeforeTransformsRef.current);

      dispatch({
        type: "START_RESIZE",
        payload: {
          edge,
          transform: {
            origin: nextOrigin,
            ...getBoundingRect(pointsRef.current, 0),
          },
        },
      });
    },
    onMove: (eventDelta) => {
      dispatch({ type: "NEW_DRAG_DELTA", payload: eventDelta });
    },
    onEnd: () => {
      let newPoints = getOutputPoints(pointsRef.current, stateRef.current);
      reduxDispatch(
        editObject({
          actionId: id,
          changes: {
            points: newPoints.map((point) =>
              Helper.transposeCoord(point, courtRef.current, "SvgToCourt")
            ),
          },
        })
      );
      dispatch({ type: "END_ACTION" });
    },
    cursor: {
      type: "resize",
      angle: angle,
      edge: edge,
    },
  });

  const outputPoints = getOutputPoints(points, state);
  const { x: posX, y: posY, width, height } = getBoundingRect(outputPoints, 0);

  const effectiveStatus = Helper.getEffectiveStatus(props);

  const showOutlines = [
    "HOVERING_ANIMATOR",
    "HOVERING",
    "SELECTED_MULTIPLE",
  ].includes(effectiveStatus);

  const showDragCircles = ["SELECTED", "INTERACTING", "EDIT"].includes(
    effectiveStatus
  );
  const showDragLines = showDragCircles && state.editMode !== "MOVE_POINT";

  return (
    <g
      id={uId}
      transform={`rotate(${state.rotate?.currentAngle ?? 0} ${
        state.rotate?.centerRectCoords?.x ?? 0
      }  ${state.rotate?.centerRectCoords?.y ?? 0})`}
    >
      <path
        /*
        onMouseEnter={() => {
          if (
            props.editable &&
            props.status === "NOT_SELECTED" &&
            !props.interaction
          ) {
            reduxDispatch(mouseEnter({ id: props.id }));
          }
        }}
        onMouseLeave={() => {
          if (
            props.editable &&
            props.status === "HOVERING" &&
            !props.interaction
          ) {
            reduxDispatch(mouseLeave({ id: props.id }));
          }
        }}*/
        style={{
          cursor: "move",
        }}
        fill={backgroundColor}
        d={getSVGPathFromPoints(outputPoints)}
      />

      {showOutlines &&
        points.map((point, index) => {
          let nextPoint = points[index + 1] ?? points[0];
          return (
            <line
              key={index}
              x1={point[0]}
              y1={point[1]}
              x2={nextPoint[0]}
              y2={nextPoint[1]}
              stroke={theme.palette.active}
              strokeWidth={2 / props.pixelPerFoot}
            />
          );
        })}

      {showDragLines && (
        <g>
          <DragLine
            {...getResizeProps("top", 0)}
            pixelPerFoot={props.pixelPerFoot}
            x1={posX}
            y1={posY}
            x2={posX + width}
            y2={posY}
          />

          <DragLine
            {...getResizeProps("bottom", 0)}
            pixelPerFoot={props.pixelPerFoot}
            x1={posX}
            y1={posY + height}
            x2={posX + width}
            y2={posY + height}
          />

          <DragLine
            {...getResizeProps("left", 0)}
            pixelPerFoot={props.pixelPerFoot}
            x1={posX}
            y1={posY + height}
            x2={posX}
            y2={posY}
          />

          <DragLine
            {...getResizeProps("right", 0)}
            pixelPerFoot={props.pixelPerFoot}
            edge="right"
            x1={posX + width}
            y1={posY}
            x2={posX + width}
            y2={posY + height}
          />

          <DragCircle
            color="white"
            {...getResizeProps("top-right", 0)}
            x={posX + width}
            y={posY}
            pixelPerFoot={props.pixelPerFoot}
          />

          <DragCircle
            color="white"
            {...getResizeProps("top-left", 0)}
            x={posX}
            y={posY}
            pixelPerFoot={props.pixelPerFoot}
          />

          <DragCircle
            color="white"
            {...getResizeProps("bottom-right", 0)}
            x={posX + width}
            y={posY + height}
            pixelPerFoot={props.pixelPerFoot}
          />

          <DragCircle
            color="white"
            {...getResizeProps("bottom-left", 0)}
            x={posX}
            y={posY + height}
            pixelPerFoot={props.pixelPerFoot}
          />
        </g>
      )}

      {showDragCircles && (
        <>
          {state.editMode !== "MOVE_POINT" && (
            <>
              <line
                x1={posX + width / 2}
                y1={posY}
                x2={posX + width / 2}
                y2={posY - 2}
                stroke={theme.palette.active}
                strokeWidth={2 / props.pixelPerFoot}
              />
              <DragCircle
                color="white"
                onStart={(e) => {
                  let rect = getBoundingRect(pointsRef.current);
                  let centerRectCoords = {
                    x: rect.x + rect.width / 2,
                    y: rect.y + rect.height / 2,
                  };

                  let centerPageCoords = SVGToScreen(centerRectCoords);

                  dispatch({
                    type: "START_ROTATE",
                    payload: {
                      centerPageCoords,
                      centerRectCoords,
                      startAngle: Helper.getAngle(centerPageCoords, {
                        x: e.clientX,
                        y: e.clientY,
                      }),
                      clientCoords: {
                        x: e.clientX,
                        y: e.clientY,
                      },
                    },
                  });
                }}
                onMove={(dragDelta, e) => {
                  dispatch({
                    type: "ROTATE_MOVE",
                    payload: {
                      clientCoords: {
                        x: e.clientX,
                        y: e.clientY,
                      },
                    },
                  });
                }}
                onEnd={() => {
                  let angle =
                    Helper.getAngle(
                      stateRef.current.rotate.centerPageCoords,
                      stateRef.current.rotate.clientCoords
                    ) - stateRef.current.rotate.startAngle;

                  let transformation = rotateObjectAroundOrigin(
                    angle,
                    stateRef.current.rotate.centerRectCoords
                  );

                  let newPoints = pointsRef.current.map((point) => {
                    point = applyTransform(point, transformation);
                    return point;
                  });

                  reduxDispatch(
                    editObject({
                      actionId: id,
                      changes: {
                        points: newPoints.map((point) =>
                          Helper.transposeCoord(
                            point,
                            courtRef.current,
                            "SvgToCourt"
                          )
                        ),
                      },
                    })
                  );
                  dispatch({ type: "END_ACTION" });
                }}
                pixelPerFoot={props.pixelPerFoot}
                x={posX + width / 2}
                y={posY - 2}
              />
            </>
          )}

          {outputPoints.map((point, index) => {
            return (
              <DragCircle
                onStart={() => {
                  dispatch({ type: "START_MOVE_POINT", payload: { index } });
                }}
                onMove={(eventDelta) => {
                  dispatch({
                    type: "NEW_DRAG_DELTA",
                    payload: eventDelta,
                  });
                }}
                onEnd={(eventDelta) => {
                  let newPoints = transformPoints(
                    pointsRef.current,
                    index,
                    eventDelta
                  );
                  reduxDispatch(
                    editObject({
                      actionId: id,
                      changes: {
                        points: newPoints.map((point) =>
                          Helper.transposeCoord(
                            point,
                            courtRef.current,
                            "SvgToCourt"
                          )
                        ),
                      },
                    })
                  );
                  dispatch({ type: "END_ACTION" });
                }}
                key={index}
                className={uId + "_resize_handle"}
                x={point[0]}
                y={point[1]}
                pixelPerFoot={pixelPerFoot}
                type="move"
                angle={angle}
              />
            );
          })}
        </>
      )}
    </g>
  );
}
